import type {
	Entity,
	FormalExpression,
	ProcessingResultsResponse,
	ProcessingStatusResponse,
	Relationship,
} from "@/types";

/**
 * Mock data for testing the ProcessingResultsPage UI
 */
export const getMockStatus = (): ProcessingStatusResponse => ({
	jobId: "mock-job-id",
	fileName: "sample-document.pdf",
	status: "completed",
	progress: 100,
	currentStep: "completed",
	step: "completed",
	createdAt: new Date().toISOString(),
	updatedAt: new Date().toISOString(),
});

export const getMockResults = (): ProcessingResultsResponse => ({
	jobId: "mock-job-id",
	status: "completed",
	results: {
		entities: [
			{
				name: "Machine Learning",
				type: "Concept",
				confidence: 0.95,
				mentions: [
					{ text: "Machine Learning", position: 120 },
					{ text: "machine learning", position: 450 },
					{ text: "ML", position: 780 },
				],
			},
			{
				name: "Neural Networks",
				type: "Technique",
				confidence: 0.88,
				mentions: [
					{ text: "Neural Networks", position: 320 },
					{ text: "neural network", position: 650 },
				],
			},
			{
				name: "Deep Learning",
				type: "Technique",
				confidence: 0.92,
				mentions: [
					{ text: "Deep Learning", position: 210 },
					{ text: "deep learning", position: 890 },
				],
			},
			{
				name: "Supervised Learning",
				type: "Method",
				confidence: 0.85,
				mentions: [{ text: "Supervised Learning", position: 540 }],
			},
			{
				name: "Classification",
				type: "Task",
				confidence: 0.9,
				mentions: [
					{ text: "Classification", position: 380 },
					{ text: "classification", position: 720 },
				],
			},
			{
				name: "CNN",
				type: "Architecture",
				confidence: 0.87,
				mentions: [
					{ text: "CNN", position: 150 },
					{ text: "Convolutional Neural Network", position: 600 },
				],
			},
			{
				name: "Transformer",
				type: "Architecture",
				confidence: 0.93,
				mentions: [
					{ text: "Transformer", position: 280 },
					{ text: "transformer architecture", position: 810 },
				],
			},
			{
				name: "Backpropagation",
				type: "Algorithm",
				confidence: 0.82,
				mentions: [{ text: "Backpropagation", position: 490 }],
			},
			{
				name: "Gradient Descent",
				type: "Algorithm",
				confidence: 0.86,
				mentions: [{ text: "Gradient Descent", position: 670 }],
			},
			{
				name: "ReLU",
				type: "Function",
				confidence: 0.79,
				mentions: [
					{ text: "ReLU", position: 230 },
					{ text: "Rectified Linear Unit", position: 560 },
				],
			},
			{
				name: "Attention Mechanism",
				type: "Mechanism",
				confidence: 0.91,
				mentions: [{ text: "Attention Mechanism", position: 410 }],
			},
			{
				name: "BERT",
				type: "Model",
				confidence: 0.89,
				mentions: [
					{ text: "BERT", position: 340 },
					{
						text: "Bidirectional Encoder Representations from Transformers",
						position: 750,
					},
				],
			},
		] as Entity[],
		relationships: [
			{
				subject: "Machine Learning",
				predicate: "includes",
				object: "Neural Networks",
				confidence: 0.92,
			},
			{
				subject: "Machine Learning",
				predicate: "includes",
				object: "Deep Learning",
				confidence: 0.9,
			},
			{
				subject: "Neural Networks",
				predicate: "uses",
				object: "Supervised Learning",
				confidence: 0.85,
			},
			{
				subject: "Deep Learning",
				predicate: "uses",
				object: "Supervised Learning",
				confidence: 0.87,
			},
			{
				subject: "Supervised Learning",
				predicate: "performs",
				object: "Classification",
				confidence: 0.88,
			},
			{
				subject: "Neural Networks",
				predicate: "implements",
				object: "CNN",
				confidence: 0.86,
			},
			{
				subject: "Deep Learning",
				predicate: "implements",
				object: "Transformer",
				confidence: 0.94,
			},
			{
				subject: "Neural Networks",
				predicate: "uses",
				object: "Backpropagation",
				confidence: 0.83,
			},
			{
				subject: "Neural Networks",
				predicate: "uses",
				object: "Gradient Descent",
				confidence: 0.84,
			},
			{
				subject: "CNN",
				predicate: "uses",
				object: "ReLU",
				confidence: 0.81,
			},
			{
				subject: "Transformer",
				predicate: "uses",
				object: "Attention Mechanism",
				confidence: 0.93,
			},
			{
				subject: "Transformer",
				predicate: "includes",
				object: "BERT",
				confidence: 0.91,
			},
		] as Relationship[],
		formalExpressions: [
			{
				expression: "∀x. MachineLearning(x) → ∃y. NeuralNetwork(y) ∧ includes(x, y)",
				type: "hol",
				confidence: 0.92,
				variables: ["x", "y"],
			},
			{
				expression: "∀x. NeuralNetwork(x) → uses(x, Backpropagation)",
				type: "hol",
				confidence: 0.85,
				variables: ["x"],
			},
			{
				expression: "∀x. DeepLearning(x) → implements(x, Transformer)",
				type: "hol",
				confidence: 0.94,
				variables: ["x"],
			},
			{
				expression: "∀x. SupervisedLearning(x) → performs(x, Classification)",
				type: "hol",
				confidence: 0.88,
				variables: ["x"],
			},
			{
				expression: "∀x. Transformer(x) → uses(x, AttentionMechanism)",
				type: "hol",
				confidence: 0.93,
				variables: ["x"],
			},
			{
				expression: "includes(Transformer, BERT)",
				type: "hol",
				confidence: 0.91,
				variables: [],
			},
		] as FormalExpression[],
		validationResults: [
			{
				expression: "∀x. MachineLearning(x) → ∃y. NeuralNetwork(y) ∧ includes(x, y)",
				valid: true,
				errors: [],
				warnings: [],
			},
			{
				expression: "∀x. NeuralNetwork(x) → uses(x, Backpropagation)",
				valid: true,
				errors: [],
				warnings: ["Consider adding quantifier scope"],
			},
		],
	},
});
